import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { FcConversionService } from '../../services/fcConversion.service';
import { PERMISSIONS } from '../../constants/permissions';
import { FcConversion, FcConversionPreview, FcConversionStatus, FcPosition } from '../../interfaces/fcBank.interface';
import { BankAccount } from '../../interfaces/bank/bankAccount.interface';
import { Segment } from '../../interfaces/accounting.interface';
import { SearchableSelect } from '../common/SearchableSelect';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { helpElement } from '../../help/attrs';
import { FILTER_CELL, bankLabel, segmentLabel, useTreasuryRefData } from './treasuryRef';

const COLORS: Record<FcConversionStatus, 'success' | 'default'> = { POSTED: 'success', REVERSED: 'default' };
const rate = (v?: number | null) => (v == null ? '—' : Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 }));

/**
 * FC bank and ERQ desk: the book position of every foreign-currency bank account (FC balance, BDT carrying amount,
 * carrying rate vs the closing rate) and the FC conversion voucher that encashes part or all of it into a BDT account at
 * the bank's deal rate, booking the realised FX. Month-end revaluation of these balances runs from the FX revaluation page.
 */
/** Help keys of the FC dialog, by kind (literals so the help key scan sees them). */
const FC_DLG_HELP: Record<'create' | 'anchor' | 'reverse', { paper: Record<string, string>; helpKey: string }> = {
  create: { paper: { 'data-help-page': 'bank.fcCreate.page' }, helpKey: 'bank.fcCreate.save' },
  anchor: { paper: { 'data-help-page': 'bank.fcAnchor.page' }, helpKey: 'bank.fcAnchor.save' },
  reverse: { paper: { 'data-help-page': 'bank.fcReverse.page' }, helpKey: 'bank.fcReverse.save' },
};

const FcConversions: React.FC = () => {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.BANK_ACCOUNT_MANAGE) && hasPermission(PERMISSIONS.JOURNAL_ENTRY_POST);
  const institutionId = AuthService.getSelectedInstitution()!;
  const ref = useTreasuryRefData(e => toast.showApiError(e, 'Failed to load reference data'));
  const [rows, setRows] = useState<FcConversion[]>([]);
  const [positions, setPositions] = useState<FcPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [dlg, setDlg] = useState<'create' | { reverse: FcConversion } | { anchor: FcPosition } | null>(null);
  const [anchorDate, setAnchorDate] = useState(toLocalIsoDate());
  const [anchorFc, setAnchorFc] = useState('');
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<FcConversionPreview | null>(null);
  const [previewKey, setPreviewKey] = useState('');

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [fcAmount, setFcAmount] = useState('');
  const [dealRate, setDealRate] = useState('');
  const [bdtReceived, setBdtReceived] = useState('');
  const [charges, setCharges] = useState('');
  const [chargesAccountId, setChargesAccountId] = useState('');
  const [segmentId, setSegmentId] = useState('');
  const [date, setDate] = useState(toLocalIsoDate());
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [list, pos] = await Promise.all([
        FcConversionService.list(institutionId, status || undefined),
        FcConversionService.positions(institutionId),
      ]);
      setRows(list);
      setPositions(pos);
    } catch (e) {
      toast.showApiError(e, 'Failed to load FC bank data');
    } finally {
      setLoading(false);
    }
  }, [institutionId, status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const hasCharges = Number(charges) > 0;
  const body = () => ({
    institutionId, fromBankAccountId: from, toBankAccountId: to, conversionDate: date || undefined,
    fcAmount: Number(fcAmount), dealRate: Number(dealRate),
    bdtReceived: bdtReceived ? Number(bdtReceived) : undefined,
    charges: hasCharges ? Number(charges) : undefined, chargesAccountId: hasCharges && chargesAccountId ? chargesAccountId : undefined,
    segmentId: segmentId || undefined, reference: reference.trim() || undefined,
  });
  const inputKey = JSON.stringify([from, to, fcAmount, dealRate, bdtReceived, charges, date]);
  const previewStale = previewKey !== inputKey;
  const ready = !!from && !!to && Number(fcAmount) > 0 && Number(dealRate) > 0;

  // Live preview of carrying rate and realised FX as the form fills in (problems are shown, never thrown).
  useEffect(() => {
    if (dlg !== 'create' || !ready) { setPreview(null); setPreviewKey(''); return undefined; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const key = inputKey;
        const p = await FcConversionService.preview(body());
        if (alive) { setPreview(p); setPreviewKey(key); }
      } catch (e) {
        if (alive) { setPreview(null); setPreviewKey(''); }
      }
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [dlg, from, to, fcAmount, dealRate, bdtReceived, charges, date]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setFrom(''); setTo(''); setFcAmount(''); setDealRate(''); setBdtReceived(''); setCharges(''); setChargesAccountId('');
    setSegmentId(''); setDate(toLocalIsoDate()); setReference(''); setPreview(null); setPreviewKey('');
    setDlg('create');
  };

  const save = async () => {
    if (!dlg) return;
    try {
      setSaving(true);
      if (dlg === 'create') {
        await FcConversionService.create(body());
      } else if ('anchor' in dlg) {
        await FcConversionService.setAnchor(institutionId, dlg.anchor.bankAccountId, anchorDate, Number(anchorFc));
      } else {
        await FcConversionService.reverse((dlg as { reverse: FcConversion }).reverse.id, reason.trim() || undefined);
      }
      toast.showSuccess('Saved');
      setDlg(null);
      await load();
    } catch (e) {
      toast.showApiError(e, 'FC conversion failed');
    } finally {
      setSaving(false);
    }
  };

  const isBdt = (b: BankAccount) => !b.currencyCode || b.currencyCode.trim().toUpperCase() === 'BDT';
  const fcBanks = ref.banks.filter(b => !isBdt(b));
  const selectedFc = fcBanks.find(b => b.id === from);
  const blocked = (preview?.problems.length ?? 0) > 0;

  return (
    <Box data-help-page="banking.fcConv.page" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">FC bank and ERQ</Typography>
        {canManage && <Button data-help="banking.fcConv.new" variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New FC conversion</Button>}
      </Box>

      {loading ? <CircularProgress /> : (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>Foreign-currency accounts</Typography>
          <TableContainer data-help-section="banking.fcConv.positions" component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="banking.fcConv.pos.col.account">Account</TableCell><TableCell data-help="banking.fcConv.pos.col.currency">Currency</TableCell>
                  <TableCell data-help="banking.fcConv.pos.col.fcBalance" align="right">FC balance</TableCell><TableCell data-help="banking.fcConv.pos.col.bdt" align="right">BDT carrying</TableCell>
                  <TableCell data-help="banking.fcConv.pos.col.bookRate" align="right">Book rate</TableCell><TableCell data-help="banking.fcConv.pos.col.closingRate" align="right">Closing rate</TableCell>
                  <TableCell data-help="banking.fcConv.pos.col.unrealised" align="right">Unrealised if revalued</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {positions.map(p => (
                  <TableRow key={p.bankAccountId} hover>
                    <TableCell>
                      {p.label}
                      {p.accountType && <Chip size="small" sx={{ ml: 1 }} label={p.accountType} />}
                      {p.anchorDate && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          FC opening balance {formatMoney(p.anchorFc, p.currencyCode)} as of {formatDate(p.anchorDate)}
                        </Typography>
                      )}
                      {canManage && (p.warnings.length > 0 || p.anchorDate) && (
                        <Button data-help="banking.fcConv.action.anchor" size="small" onClick={() => { setAnchorDate(toLocalIsoDate()); setAnchorFc(p.anchorFc != null ? String(p.anchorFc) : ''); setDlg({ anchor: p }); }}>
                          Set FC opening balance
                        </Button>
                      )}
                      {p.warnings.map(w => <Typography key={w} variant="caption" color="warning.main" display="block">{w}</Typography>)}
                    </TableCell>
                    <TableCell>{p.currencyCode}</TableCell>
                    <TableCell align="right">{formatMoney(p.fcBalance, p.currencyCode)}</TableCell>
                    <TableCell align="right">{formatMoney(p.bdtBalance)}</TableCell>
                    <TableCell align="right">{rate(p.carryingRate)}</TableCell>
                    <TableCell align="right">{rate(p.closingRate)}{p.closingRateDate ? ` (${formatDate(p.closingRateDate)})` : ''}</TableCell>
                    <TableCell align="right">{p.unrealisedGainLoss == null ? '—' : formatMoney(p.unrealisedGainLoss)}</TableCell>
                  </TableRow>
                ))}
                {positions.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center">
                    No foreign-currency bank accounts. Set a currency (and type ERQ or FC) on a bank account to track it here.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" sx={{ mb: 1 }}>FC conversions</Typography>
          <Box data-help-section="banking.fcConv.filter" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <TextField data-help="banking.fcConv.statusFilter" select label="Status" value={status} onChange={e => setStatus(e.target.value)} sx={FILTER_CELL}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="POSTED">Posted</MenuItem>
              <MenuItem value="REVERSED">Reversed</MenuItem>
            </TextField>
          </Box>
          <TableContainer data-help-section="banking.fcConv.table" component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="banking.fcConv.col.number">No.</TableCell><TableCell data-help="banking.fcConv.col.date">Date</TableCell><TableCell data-help="banking.fcConv.col.from">From</TableCell><TableCell data-help="banking.fcConv.col.to">To</TableCell>
                  <TableCell data-help="banking.fcConv.col.fcAmount" align="right">FC amount</TableCell><TableCell data-help="banking.fcConv.col.dealRate" align="right">Deal rate</TableCell>
                  <TableCell data-help="banking.fcConv.col.bdtReceived" align="right">BDT received</TableCell><TableCell data-help="banking.fcConv.col.fx" align="right">Realised FX</TableCell>
                  <TableCell data-help="banking.fcConv.col.status">Status</TableCell><TableCell data-help="banking.fcConv.col.actions">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(c => (
                  <TableRow key={c.id} hover>
                    <TableCell>{c.conversionNumber}</TableCell>
                    <TableCell>{formatDate(c.conversionDate)}</TableCell>
                    <TableCell>{c.fromLabel}</TableCell>
                    <TableCell>{c.toLabel}</TableCell>
                    <TableCell align="right">{formatMoney(c.fcAmount, c.currencyCode)}</TableCell>
                    <TableCell align="right">{rate(c.dealRate)}</TableCell>
                    <TableCell align="right">{formatMoney(c.bdtReceived)}</TableCell>
                    <TableCell align="right" data-testid={`fx-${c.id}`}>
                      {c.fxGainLoss === 0 ? '—' : `${c.fxGainLoss > 0 ? 'Gain ' : 'Loss '}${formatMoney(Math.abs(c.fxGainLoss))}`}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={humanizeEnum(c.status)} color={COLORS[c.status]} />
                      {c.reversalReason && <Typography variant="caption" display="block">{c.reversalReason}</Typography>}
                    </TableCell>
                    <TableCell>
                      {c.status === 'POSTED' && canManage && (
                        <Button data-help="banking.fcConv.action.reverse" size="small" color="warning" onClick={() => { setReason(''); setDlg({ reverse: c }); }}>Reverse</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={10} align="center">No FC conversions.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={!!dlg} onClose={() => setDlg(null)} maxWidth="sm" fullWidth PaperProps={dlg ? FC_DLG_HELP[dlg === 'create' ? 'create' : 'anchor' in dlg ? 'anchor' : 'reverse'].paper as any : undefined}>
        <DialogTitle>{dlg === 'create' ? 'New FC conversion' : dlg && 'anchor' in dlg ? `FC opening balance: ${dlg.anchor.label}` : dlg ? `Reverse ${dlg.reverse.conversionNumber}` : ''}</DialogTitle>
        {dlg === 'create' && (
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }} data-help-section="bank.fcCreate.section.body">
            <SearchableSelect<BankAccount> data-help="bank.fcCreate.field.from" label="From FC / ERQ account" options={fcBanks} value={from} required
              getOptionLabel={bankLabel} onChange={id => setFrom(id)} />
            <SearchableSelect<BankAccount> data-help="bank.fcCreate.field.to" label="To BDT bank account" options={ref.banks.filter(isBdt)}
              value={to} required getOptionLabel={bankLabel} onChange={id => setTo(id)} />
            <TextField data-help="bank.fcCreate.field.fcAmount" label={`FC amount${selectedFc?.currencyCode ? ` (${selectedFc.currencyCode})` : ''}`} type="number" value={fcAmount} required
              fullWidth onChange={e => setFcAmount(e.target.value)} />
            <TextField data-help="bank.fcCreate.field.dealRate" label="Deal rate (BDT per unit)" type="number" value={dealRate} required fullWidth
              onChange={e => setDealRate(e.target.value)} />
            <TextField data-help="bank.fcCreate.field.bdtReceived" label="BDT received (optional, net of charges)" type="number" value={bdtReceived} fullWidth
              helperText="Leave empty to use FC amount x deal rate less charges." onChange={e => setBdtReceived(e.target.value)} />
            <TextField data-help="bank.fcCreate.field.charges" label="Bank charges (optional)" type="number" value={charges} fullWidth onChange={e => setCharges(e.target.value)} />
            {hasCharges && (
              <AccountAutocomplete data-help="bank.fcCreate.field.chargesAccount" accounts={ref.expenses} value={chargesAccountId} label="Charges expense account (default from treasury settings)"
                onChange={id => setChargesAccountId(id)} />
            )}
            <SearchableSelect<Segment> data-help="bank.fcCreate.field.segment" label="Segment" options={ref.segments} value={segmentId} getOptionLabel={segmentLabel}
              onChange={id => setSegmentId(id)} />
            <TextField data-help="bank.fcCreate.field.date" label="Date" type="date" value={date} fullWidth InputLabelProps={{ shrink: true }}
              inputProps={{ max: toLocalIsoDate() }} onChange={e => setDate(e.target.value)} />
            <TextField data-help="bank.fcCreate.field.reference" label="Reference" value={reference} fullWidth inputProps={{ maxLength: 120 }}
              onChange={e => setReference(e.target.value)} />
            {preview && (
              <Box data-testid="fc-preview">
                {preview.problems.map(p => <Alert key={p} severity="error" sx={{ mb: 1 }}>{p}</Alert>)}
                {preview.carryingRate != null && (
                  <Typography variant="body2">
                    Account holds {formatMoney(preview.fcBalance, preview.currencyCode)} carried at {rate(preview.carryingRate)}
                    {' '}({formatMoney(preview.bdtBalance)}).
                  </Typography>
                )}
                {preview.fxGainLoss != null && (
                  <Typography variant="body2">
                    Carrying amount of this conversion {formatMoney(preview.carryingAmount)}; realised FX{' '}
                    <strong>{preview.fxGainLoss === 0 ? 'nil' : `${preview.fxGainLoss > 0 ? 'gain' : 'loss'} ${formatMoney(Math.abs(preview.fxGainLoss))}`}</strong>.
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
        )}
        {dlg && dlg !== 'create' && 'anchor' in dlg && (
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }} data-help-section="bank.fcAnchor.section.body">
            <Typography variant="body2">
              Some entries on this account have no {dlg.anchor.currencyCode} amount (an opening balance, or a charge booked in BDT) and
              posted entries cannot be edited. State the true {dlg.anchor.currencyCode} balance on a date after those entries: from then
              on the balance is that figure plus the entries dated after it.
            </Typography>
            <TextField data-help="bank.fcAnchor.field.date" label="As of date" type="date" value={anchorDate} fullWidth InputLabelProps={{ shrink: true }}
              inputProps={{ max: toLocalIsoDate() }} onChange={e => setAnchorDate(e.target.value)} />
            <TextField data-help="bank.fcAnchor.field.balance" label={`True ${dlg.anchor.currencyCode} balance on that date`} type="number" value={anchorFc} required fullWidth
              onChange={e => setAnchorFc(e.target.value)} />
          </DialogContent>
        )}
        {dlg && dlg !== 'create' && 'reverse' in dlg && (
          <DialogContent sx={{ pt: '8px !important' }} data-help-section="bank.fcReverse.section.body">
            <Typography variant="body2" sx={{ mb: 2 }}>
              Posts an exact mirror journal (FC memo included), so the FC balance and the BDT account return to where they were.
            </Typography>
            <TextField data-help="bank.fcReverse.field.reason" label="Reason" value={reason} fullWidth multiline minRows={2} onChange={e => setReason(e.target.value)} />
          </DialogContent>
        )}
        <DialogActions>
          <Button data-help="bank.fcConv.dlgClose" onClick={() => setDlg(null)}>Close</Button>
          <Button {...helpElement(dlg ? FC_DLG_HELP[dlg === 'create' ? 'create' : 'anchor' in dlg ? 'anchor' : 'reverse'].helpKey : undefined)} variant="contained" disabled={saving || (dlg === 'create' && (!ready || blocked || previewStale))
            || (!!dlg && dlg !== 'create' && 'anchor' in dlg && (!anchorDate || anchorFc === ''))} onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FcConversions;
